/**
 * components/landing/ai-platform/sections/AIMentor.jsx — §4.4 AI Mentor deep dive.
 *
 * Two-column layout — capability list on the left, a live-ish mentor
 * conversation UI on the right cycling through 3 grounded exchanges.
 */

import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import {
  Compass, Map, Sparkles, Building2, CornerDownRight, GitBranch, Bot,
  Send, Zap, PlayCircle,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { MENTOR_CAPS } from '../data'
import { TONE } from '../_shared'

const ICONS = { Compass, Map, Sparkles, Building2, CornerDownRight, GitBranch }

const CONVERSATIONS = [
  {
    context: 'module=strategy · ticker=MES',
    q: 'Why is my MES strategy underperforming this week?',
    a: 'Regime shifted to low-vol range-bound on Wed. Your breakout entries are firing 3× more but win-rate dropped from 58% → 41%.',
    meta: '2 citations · 1 chart · 1 action',
    followups: ['Show me the regime chart', 'Suggest an entry filter', 'Compare Q2 vs Q3'],
  },
  {
    context: 'module=academy · role=trader',
    q: 'What should I learn next?',
    a: 'Your quiz results show gaps in position sizing under regime uncertainty. Recommend “Regime-based Kelly” — 30 mins, 3 practical.',
    meta: '1 citation · 1 recommended lesson',
    followups: ['Start lesson', 'Show my progress', 'Assign to team'],
  },
  {
    context: 'module=analytics · role=admin',
    q: 'What drove MRR growth this quarter?',
    a: 'Two effects: (1) 11% seat expansion in white-label tenants; (2) Academy attach-rate rose 6pp — lifting the Growth tier.',
    meta: '3 cohorts · 2 charts · SQL exportable',
    followups: ['Export report', 'Cohort by module usage', 'Forecast next quarter'],
  },
]

export default function AIMentor() {
  return (
    <section id="ai-mentor" className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>AI Mentor</Eyebrow>
          <SectionHeading
            align="center"
            title={<>Your senior analyst — <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">on demand.</span></>}
            subtitle="AI Mentor is the conversational surface that stitches every module together. It reads the current screen, retrieves the right context, and answers with citations — not with confident hallucinations."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
          {/* capabilities */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {MENTOR_CAPS.map((c) => {
              const tone = TONE[c.tone] || TONE.orange
              const Icon = ICONS[c.iconKey] || Sparkles
              return (
                <div key={c.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.03] transition-all">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border"
                      style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                      <Icon style={{ width: 14, height: 14 }} strokeWidth={1.8} />
                    </span>
                    <h4 className="text-[13.5px] font-semibold text-white leading-tight">{c.label}</h4>
                  </div>
                  <p className="text-[12.5px] text-zinc-400 leading-relaxed mt-2">{c.body}</p>
                </div>
              )
            })}
          </div>

          {/* live-ish mentor UI */}
          <MentorPreview />
        </div>
      </Container>
    </section>
  )
}

function MentorPreview() {
  const [i, setI] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % CONVERSATIONS.length), 6500)
    return () => clearInterval(t)
  }, [])
  const c = CONVERSATIONS[i]
  return (
    <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-5 md:p-6 backdrop-blur-xl overflow-hidden relative">
      <div className="pointer-events-none absolute inset-0 opacity-70"
        style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.10), transparent 70%)' }} />
      <div className="relative flex flex-col gap-3">
        {/* chrome */}
        <div className="flex items-center gap-1.5 rounded-md bg-black/40 px-2.5 py-2 border border-white/5">
          <div className="h-2 w-2 rounded-full bg-red-500/70" />
          <div className="h-2 w-2 rounded-full bg-amber-500/70" />
          <div className="h-2 w-2 rounded-full bg-emerald-500/70" />
          <div className="ml-3 flex-1 h-5 rounded-md bg-white/[0.04] flex items-center px-2 text-[10px] font-mono text-zinc-500">
            app.kalkiai.trade/mentor
          </div>
          <div className="flex items-center gap-1 text-[10px] font-mono text-emerald-400 pr-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> live
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <Bot className="h-3.5 w-3.5 text-brand-gold" /> AI Mentor · session
          <span className="ml-auto text-[10px] font-mono text-zinc-500">{c.context}</span>
        </div>

        <div key={i} className="flex flex-col gap-2 min-h-[220px] animate-brand-fade-up">
          <div className="self-start max-w-[85%] rounded-lg rounded-tl-none border border-white/10 bg-white/[0.03] px-3 py-2 text-[13px] text-zinc-200">
            {c.q}
          </div>
          <div className="self-end max-w-[85%] rounded-lg rounded-tr-none border border-brand-gold/25 bg-brand-gold/[0.08] px-3 py-2 text-[13px] text-zinc-100">
            {c.a}
            <div className="mt-1.5 text-[10px] text-zinc-400 font-mono">▸ {c.meta}</div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {c.followups.map((f) => (
              <button key={f} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] text-zinc-300 hover:border-brand-gold/30 hover:text-white">
                <CornerDownRight className="h-3 w-3" /> {f}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/50 px-2.5 py-1.5">
          <div className="flex-1 text-[11px] text-zinc-500">Ask the mentor anything…</div>
          <Send className="h-3.5 w-3.5 text-brand-gold" />
        </div>

        <div className="flex items-center justify-center gap-1.5">
          {CONVERSATIONS.map((_, idx) => (
            <span key={idx} className={clsx('h-1.5 rounded-full transition-all', idx === i ? 'w-8 bg-brand-gold' : 'w-1.5 bg-white/20')} />
          ))}
        </div>
      </div>
    </div>
  )
}
